/**
 * Shared manifest frontmatter schemas and types.
 * Common across all 11 asset kinds + Preset.
 */
import * as z from "zod";

// ---------------------------------------------------------------------------
// Asset kind enum
// ---------------------------------------------------------------------------

export const AssetKindValues = [
  "skill",
  "agent",
  "connector",
  "mount",
  "flow",
  "contract",
  "prompt",
  "mcp-server",
  "knowledge",
  "persona",
  "ruleset",
] as const;

export const AssetKindSchema = z.enum(AssetKindValues);
export type AssetKind = z.infer<typeof AssetKindSchema>;

// ---------------------------------------------------------------------------
// Shared metadata sub-schemas
// ---------------------------------------------------------------------------

/** Artifact dependency (requires/produces/consumes in skill metadata). */
export const ArtifactRefSchema = z.object({
  id: z.string().min(1),
  description: z.string().optional(),
  gate: z.enum(["hard", "soft"]).optional(),
  path: z.string().optional(),
});

/** File prerequisite for a skill. */
export const FilePrerequisiteSchema = z.object({
  path: z.string().min(1),
  gate: z.enum(["hard", "soft"]).optional(),
  description: z.string().optional(),
});

/** User input definition (for skill user_inputs or metadata.prerequisites.inputs_*). */
export const UserInputSchema = z.looseObject({
  key: z.string().min(1).optional(),
  id: z.string().min(1).optional(),
  label: z.string().optional(),
  prompt: z.string().optional(),
  type: z.string().optional(),
  required: z.boolean().optional(),
  options: z.array(z.string()).optional(),
  values: z.array(z.union([z.string(), z.number()])).optional(),
  default: z
    .union([z.string(), z.number(), z.boolean(), z.array(z.union([z.string(), z.number()]))])
    .optional(),
  hint: z.string().optional(),
});

/** Field definition for connectors/mounts. */
export const FieldSchema = z.object({
  key: z.string().min(1),
  label: z.string().optional(),
  required: z.boolean().optional(),
  sensitive: z.boolean().optional(),
  type: z.string().optional(),
  default: z.union([z.string(), z.number(), z.boolean()]).optional(),
});

// ---------------------------------------------------------------------------
// Common metadata block (appears inside metadata: in most manifest kinds)
// ---------------------------------------------------------------------------

export const CommonMetadataSchema = z.looseObject({
  version: z.string().optional(),
  author: z.string().optional(),
  tags: z.array(z.string()).optional(),
  stage: z.enum(["alpha", "beta", "stable", "deprecated"]).optional(),
  source: z.string().optional(),
  do_not_invoke: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Common frontmatter fields (top-level, shared across kinds)
// ---------------------------------------------------------------------------

/**
 * Fields that can appear at the top level of any manifest's frontmatter.
 * Not all are required -- each kind schema intersects with its own specifics.
 */
export const BaseFrontmatterSchema = z.object({
  name: z.string().min(1),
  description: z.string().min(1),
  version: z.string().optional(),

  // Catalog identity (populated at discovery or authoring time)
  publisher: z.string().optional(),
  category: z.string().optional(),
  license: z.string().optional(),
  keywords: z.array(z.string()).optional(),
});

// ---------------------------------------------------------------------------
// Validation result type
// ---------------------------------------------------------------------------

export interface ManifestValidationSuccess<T> {
  ok: true;
  data: T;
}

export interface ManifestValidationFailure {
  ok: false;
  errors: Array<{ path: string; message: string }>;
}

export type ManifestValidationResult<T> = ManifestValidationSuccess<T> | ManifestValidationFailure;

export function validateManifest<T>(
  schema: z.ZodType<T>,
  data: unknown,
): ManifestValidationResult<T> {
  const result = schema.safeParse(data);
  if (result.success) {
    return { ok: true, data: result.data };
  }
  return {
    ok: false,
    errors: result.error.issues.map((issue) => ({
      path: issue.path.join("."),
      message: issue.message,
    })),
  };
}
